import { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { UserPlus, Edit, Trash2, Shield, User as UserIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import { 
  fetchAdmins as fetchAdminsService, 
  createAdmin, 
  deleteAdmin as deleteAdminService,
  checkEmailExists 
} from '../services/adminService';
import Card, { CardBody } from '../components/ui/Card';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { FormField, Input } from '../components/ui/FormField';
import { Badge } from '../components/ui/Badge';
import { useToast } from '../components/ui/Toast';

/**
 * Admins Management Page
 * Only accessible to admin_master
 * CRUD operations for admin users
 */

export default function Admins() {
  const { user, isMasterAdmin } = useAuth();
  const { toast } = useToast();
  
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    type: 'admin',
  });

  // Fetch admins from Firestore
  useEffect(() => {
    if (isMasterAdmin) {
      fetchAdmins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMasterAdmin]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const adminsList = await fetchAdminsService();
      setAdmins(adminsList);
      setCurrentPage(1); // Reset to first page when data refreshes
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(admins.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAdmins = admins.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    
    // Prevent adding admin_master
    if (formData.type === 'admin_master') {
      toast.error('Cannot add new Master Admin. Only regular admins can be created.');
      return;
    }

    setSubmitting(true);

    try {
      // Check if email already exists
      const emailExists = await checkEmailExists(formData.email);
      if (emailExists) {
        toast.error('Email already exists');
        setSubmitting(false);
        return;
      }

      // Call Cloud Function to create admin
      await createAdmin({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        type: formData.type === 'operator' ? 'operator' : 'admin',
      });

      logAdminActivity({
        actor: user, action: 'create_admin',
        targetType: 'admin', targetId: null,
        targetLabel: `${formData.name} · ${formData.email} (${formData.type})`,
      });
      toast.success('Admin created successfully');
      setShowAddModal(false);
      resetForm();
      fetchAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      
      // User-friendly error messages
      const errorMessage = error.message || '';
      
      if (error.code === 'functions/already-exists' || errorMessage.includes('Email already in use')) {
        toast.error('This email is already registered. Please use a different email address.');
      } else if (error.message.includes('Cloud Functions')) {
        toast.error('Cloud Functions not set up. Please contact administrator.');
      } else if (error.code === 'auth/email-already-in-use' || error.code === 'functions/already-exists') {
        toast.error('Email already in use. Please use a different email.');
      } else if (error.code === 'auth/weak-password' || error.code === 'functions/invalid-argument') {
        toast.error('Password is too weak. Use at least 6 characters.');
      } else if (error.code === 'functions/permission-denied') {
        toast.error('Permission denied. Only Master Admin can create admins.');
      } else if (error.code === 'functions/unauthenticated') {
        toast.error('You must be logged in to create admins.');
      } else {
        toast.error(errorMessage || 'Failed to add admin. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const userRef = doc(db, 'users', selectedAdmin.id);
      
      await updateDoc(userRef, {
        name: formData.name,
        email: formData.email,
        type: formData.type,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
      });

      logAdminActivity({
        actor: user, action: 'update_admin',
        targetType: 'admin', targetId: selectedAdmin.id,
        targetLabel: `${formData.name} · ${formData.email}`,
      });
      toast.success('Admin updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin:', error);
      toast.error('Failed to update admin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAdmin = async () => {
    setSubmitting(true);

    try {
      // Use Cloud Function to delete from both Auth and Firestore
      await deleteAdminService(selectedAdmin.id);

      logAdminActivity({
        actor: user, action: 'delete_admin',
        targetType: 'admin', targetId: selectedAdmin.id,
        targetLabel: `${selectedAdmin.name || ''} · ${selectedAdmin.email || ''}`.trim(),
      });
      toast.success('Admin deleted successfully');
      setShowDeleteModal(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      
      if (error.message.includes('Cloud Functions')) {
        toast.error('Cloud Functions not set up. Please contact administrator.');
      } else {
        toast.error(error.message || 'Failed to delete admin');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '',
      type: admin.type,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (admin) => {
    setSelectedAdmin(admin);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      type: 'admin',
    });
    setSelectedAdmin(null);
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center border border-primary-200">
            <UserIcon className="w-5 h-5 text-primary-600" />
          </div>
          <span className="font-semibold text-slate-900">{row.name}</span>
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
    },
    {
      key: 'type',
      label: 'Role',
      render: (row) => (
        <Badge
          variant={
            row.type === 'admin_master'
              ? 'secondary'
              : row.type === 'operator'
              ? 'success'
              : 'primary'
          }
        >
          {row.type === 'admin_master' ? (
            <>
              <Shield className="w-3 h-3" />
              Master Admin
            </>
          ) : row.type === 'operator' ? (
            'Operator'
          ) : (
            'Admin'
          )}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (row) => {
        if (!row.createdAt) return '-';
        return new Date(row.createdAt.seconds * 1000).toLocaleDateString();
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => {
        // Can't edit or delete master admin
        if (row.type === 'admin_master') {
          return <span className="text-sm text-gray-400">Protected</span>;
        }
        
        return (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => openEditModal(row)}
              leftIcon={<Edit className="w-4 h-4" />}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => openDeleteModal(row)}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  if (!isMasterAdmin) {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 bg-gradient-to-br from-danger-100 to-danger-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
          <Shield className="w-10 h-10 text-danger-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-3">Access Denied</h2>
        <p className="text-slate-600">Only Master Admin can manage administrators.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Admins Management</h1>
          <p className="text-slate-600 mt-2">Manage admin users and their access</p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowAddModal(true)}
          leftIcon={<UserPlus className="w-5 h-5" />}
        >
          Add Admin
        </Button>
      </div>

      {/* Admins Table */}
      <Card>
        <CardBody className="p-0">
          <Table
            columns={columns}
            data={currentAdmins}
            loading={loading}
            emptyMessage="No admins found"
          />
          
          {/* Pagination */}
          {!loading && admins.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Showing {startIndex + 1} to {Math.min(endIndex, admins.length)} of {admins.length} admins
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                            currentPage === page
                              ? 'bg-primary-600 text-white'
                              : 'text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span key={page} className="text-slate-400 px-2">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  rightIcon={<ChevronRight className="w-4 h-4" />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Add Admin Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          resetForm();
        }}
        title="Add New Admin"
      >
        <form onSubmit={handleAddAdmin} className="space-y-4">
          <FormField label="Full Name" required>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter admin name"
              required
            />
          </FormField>

          <FormField label="Email Address" required>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="admin@dalverha.com"
              required
            />
          </FormField>

          <FormField label="Password" required>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Minimum 6 characters"
              minLength={6}
              required
            />
          </FormField>

          <FormField label="Role" required>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-4 py-3 text-sm border-2 border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent hover:border-slate-300 transition-all"
              required
            >
              <option value="admin">Admin</option>
              <option value="operator">Operator</option>
            </select>
            <p className="mt-2 text-xs text-slate-500">
              Operators have access to Orders, Customers, Drivers, Blocked Numbers, Transactions,
              Ratings, Delete Requests, Map and Notifications only.
            </p>
          </FormField>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                resetForm();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              className="flex-1"
            >
              Add Admin
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Admin Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          resetForm();
        }}
        title="Edit Admin"
      >
        <form onSubmit={handleUpdateAdmin} className="space-y-4">
          <FormField label="Full Name" required>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </FormField>

          <FormField label="Email Address" required>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled
              helperText="Email cannot be changed"
            />
          </FormField>

          <FormField label="Role" required>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-4 py-3 text-sm border-2 border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent hover:border-slate-300 transition-all"
              required
              disabled={selectedAdmin?.type === 'admin_master'}
            >
              <option value="admin">Admin</option>
              <option value="operator">Operator</option>
            </select>
            <p className="mt-2 text-xs text-slate-500">Master Admin role is protected and cannot be changed.</p>
          </FormField>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              className="flex-1"
            >
              Update Admin
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Admin"
      >
        <div className="space-y-4">
          <p className="text-slate-700">
            Are you sure you want to delete <strong className="text-slate-900">{selectedAdmin?.name}</strong>?
            This action cannot be undone.
          </p>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteAdmin}
              loading={submitting}
              className="flex-1"
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}